import { useHasInsetChrome } from "@circle-react-uikit/CircleAIAppShell/hooks/useHasInsetChrome";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { PropsWithChildren } from "react";

import { PAGE_SURFACE } from "@/react/constants/pageSurface";

/**
 * The rounded primary content card of the canvas "double container". While the
 * AI panel is open, `-mx-px` widens this stretched flex item by 1px per side so
 * its top + side borders land exactly on the inset's outer border (single
 * merged line, no double border); `border-b-0` leaves the bottom to the inset's
 * own rounded border, and `overflow-hidden` clips scrolled content to the
 * rounded top corners. The tinted inset shows through the rounded corner notches.
 *
 * Lays its children out as a column: an optional sub-header (e.g. the member
 * shell's app header) stacks above the body content. Consumers that need a row
 * (sidebar | workspace | rail) nest their own `Inset.Body` inside.
 *
 * Must remain a direct child of `Inset` for the `-mx-px` border merge to work —
 * wrapping it in another element re-clips the 1px overlap and the border doubles.
 */
export const CanvasInsetBody = ({ children }: Readonly<PropsWithChildren>) => {
  const hasInsetChrome = useHasInsetChrome();

  return (
    <div
      className={classNames(
        "flex min-h-0 flex-1 flex-col overflow-hidden",
        hasInsetChrome &&
          "border-primary -mx-px rounded-t-lg border border-b-0",
        hasInsetChrome && PAGE_SURFACE,
      )}
    >
      {children}
    </div>
  );
};

CanvasInsetBody.displayName = "CanvasInsetBody";
